import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const STORAGE_KEY = "astrokuber:ongoing-chat-id";
const OngoingChatContext = createContext(null);

export function OngoingChatProvider({ children }) {
  const { user, checkAuth } = useAuth();
  const [activeChat, setActiveChat] = useState(null);
  const [billing, setBilling] = useState(null);
  const [lastMessage, setLastMessage] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const hbRef = useRef(null);
  const pollRef = useRef(null);
  const lastTsRef = useRef(null);
  const pathRef = useRef(typeof window !== "undefined" ? window.location.pathname : "");

  // Track current path so the poll can decide whether to increment unread.
  useEffect(() => {
    const update = () => { pathRef.current = window.location.pathname; };
    window.addEventListener("popstate", update);
    window.addEventListener("pushstate", update);
    const id = setInterval(update, 800); // SPA pathname change detector
    return () => {
      window.removeEventListener("popstate", update);
      window.removeEventListener("pushstate", update);
      clearInterval(id);
    };
  }, []);

  // Restore from localStorage on user load
  useEffect(() => {
    if (!user) {
      setActiveChat(null);
      setBilling(null);
      try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
      return;
    }
    const storedId = (() => {
      try { return localStorage.getItem(STORAGE_KEY); } catch (_) { return null; }
    })();
    if (!storedId) return;
    (async () => {
      try {
        const { data: chat } = await api.get(`/chats/${storedId}`);
        if (chat?.status === "active") {
          setActiveChat({
            chat_id: chat.chat_id,
            astrologer_id: chat.astrologer_id,
            astrologer_name: chat.astrologer_name,
            astrologer_avatar: chat.astrologer_avatar,
            astrologer_first_name: (chat.astrologer_name || "").split(" ")[0],
          });
        } else {
          try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
        }
      } catch (_) {
        try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
      }
    })();
  }, [user]);

  const registerActiveChat = useCallback((chat) => {
    if (!chat?.chat_id) return;
    setActiveChat({
      chat_id: chat.chat_id,
      astrologer_id: chat.astrologer_id,
      astrologer_name: chat.astrologer_name,
      astrologer_avatar: chat.astrologer_avatar,
      astrologer_first_name: (chat.astrologer_name || "").split(" ")[0],
    });
    try { localStorage.setItem(STORAGE_KEY, chat.chat_id); } catch (_) {}
  }, []);

  const clearActiveChat = useCallback(() => {
    setActiveChat(null);
    setBilling(null);
    setLastMessage(null);
    setUnreadCount(0);
    lastTsRef.current = null;
    try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
  }, []);

  const markRead = useCallback(() => {
    setUnreadCount(0);
  }, []);

  // Global heartbeat — fires only when activeChat is set. The backend
  // heartbeat is idempotent (settles only unbilled elapsed time), so it is
  // safe to run alongside the ChatPage heartbeat when the page is open.
  useEffect(() => {
    if (!activeChat?.chat_id) {
      if (hbRef.current) clearInterval(hbRef.current);
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.post(`/chats/${activeChat.chat_id}/billing/heartbeat`);
        if (cancelled) return;
        setBilling({ ...data, _heartbeat_ts: Date.now() });
        // Mirror the chat's CURRENT status onto the dock so the UI morphs
        // from "Connecting…" → "Live in session" the moment the astrologer
        // accepts (even while the user is on a different page). Without
        // this the dock stays stuck on the ringing pill until the user
        // re-enters the chat page.
        if (data.status && data.status !== activeChat.status) {
          setActiveChat((prev) => prev ? { ...prev, status: data.status } : prev);
        }
        // The heartbeat response carries the chat's CURRENT status. If the
        // astrologer ended the chat (or it auto-ended on balance exhaustion),
        // tear down the dock immediately — otherwise the persistent "Tap to
        // continue the conversation" dock keeps surfacing on every page and
        // routes the user to a stale chat document.
        if (data.status === "ended" || data.auto_ended) {
          clearActiveChat();
          checkAuth?.();
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404 || status === 400) {
          clearActiveChat();
        }
      }
    };
    tick();
    // Faster cadence (3s) while the chat is still ringing so the dock
    // morphs from "Connecting…" → "Live in session" almost instantly
    // when the astrologer accepts — even if the user is on a different
    // page. Active chats only need a settling beat every 15s.
    const interval = activeChat?.status === "ringing" ? 3000 : 15000;
    hbRef.current = setInterval(tick, interval);
    return () => { cancelled = true; if (hbRef.current) clearInterval(hbRef.current); };
  }, [activeChat?.chat_id, activeChat?.status, clearActiveChat, checkAuth]);

  // Poll the latest message every 4s so the dock can show a live preview of
  // the astrologer's most recent line (and update unread counter etc.).
  useEffect(() => {
    if (!activeChat?.chat_id) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    let cancelled = false;
    const pull = async () => {
      try {
        const params = lastTsRef.current ? { after: lastTsRef.current } : {};
        const { data } = await api.get(`/chats/${activeChat.chat_id}/messages`, { params });
        if (cancelled || !Array.isArray(data) || data.length === 0) return;
        const latest = data[data.length - 1];
        lastTsRef.current = latest.created_at;
        // Only display text messages in the dock preview (skip kundali_chart cards)
        const textMsgs = data.filter((m) => (m.kind || "text") === "text");
        if (textMsgs.length > 0) {
          const last = textMsgs[textMsgs.length - 1];
          setLastMessage({
            sender_role: last.sender_role,
            text: last.text || "",
            created_at: last.created_at,
          });
          // Increment unread for non-customer messages when user isn't on the chat page
          const onChat = pathRef.current === `/chat/${activeChat.chat_id}`;
          if (!onChat) {
            const incoming = textMsgs.filter((m) => m.sender_role !== "customer");
            if (incoming.length > 0) {
              setUnreadCount((c) => Math.min(99, c + incoming.length));
            }
          }
        }
      } catch (_) { /* ignore */ }
    };
    pull();
    pollRef.current = setInterval(pull, 4000);
    return () => { cancelled = true; if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeChat?.chat_id]);

  return (
    <OngoingChatContext.Provider value={{ activeChat, billing, lastMessage, unreadCount, registerActiveChat, clearActiveChat, markRead }}>
      {children}
    </OngoingChatContext.Provider>
  );
}

export function useOngoingChat() {
  const ctx = useContext(OngoingChatContext);
  if (!ctx) throw new Error("useOngoingChat must be used within OngoingChatProvider");
  return ctx;
}
